import { Author, Image } from "@/content/templates/template.types.ts";

type Nullable<T> = T | null;

export interface CallToActionMicrocopy {
    ctaButtonText?: Nullable<string>;
    ctaUrl?: Nullable<string>;
}

export interface CallToAction {
    __typename?: "ContentPlatformCallToAction" | "CallToAction";
    title: Nullable<string>;
    valueProposition: Nullable<string>;
    text: Nullable<string>;
    url: Nullable<string>;
    asset: Nullable<Image>;
    productName: Nullable<string>;
    productLogo: Nullable<Image>;
    product?: Nullable<Product[]>;
    dataBlueprintModule: Nullable<string>;
}

export interface TopicIntroduction {
    title: Nullable<string>;
    details: Nullable<string>;
    asset?: Nullable<Image[]>;
    embedLink?: Nullable<string>;
}

export type FeaturedContentContainer =
    | Article
    | Tutorial
    | ProductFeature
    | FeaturedVideo;

export interface Article {
    __typename?: string;
    articleName: Nullable<string>;
    title: Nullable<string>;
    description: Nullable<string>;
    urlSlug: string;
    articleBanner: Nullable<Image>;
}

export interface Tutorial {
    __typename?: string;
    name?: Nullable<string>;
    title?: Nullable<string>;
    description?: Nullable<string>;
    tutorialBanner?: Nullable<Image>;
    urlSlug: string;
}

export interface Product {
    name: Nullable<string>;
    icon: Nullable<Image>;
}

export interface ProductFeature {
    __typename?: string;
    name?: Nullable<string>;
    slogan?: Nullable<string>;
    description?: Nullable<string>;
    callToAction?: Nullable<CallToAction[]>;
}

export interface FeaturedVideo {
    __typename?: string;
    videoName?: Nullable<string>;
    description?: Nullable<string>;
    link?: Nullable<string>;
    callToActionText?: Nullable<string>;
}

export type ContentComponent =
    | TextComponent
    | AssetComponent
    | EmbedComponent
    | ProTipComponent
    | TwitterComponent
    | CallToAction
    | CallOutComponent
    | QuestionComponent;

export interface TextComponent {
    __typename?: "ContentPlatformTextComponent" | "TextComponent";
    name: Nullable<string>;
    text: Nullable<string>;
}

export interface CallOutComponent {
    __typename?: "ContentPlatformCallOutComponent" | "CallOutComponent";
    title: Nullable<string>;
    callOutText: Nullable<string>;
    icon?: Nullable<Image>;
}

export interface AssetComponent {
    __typename?: "ContentPlatformAssetComponent" | "AssetComponent";
    asset?: Nullable<Image>;
    caption?: Nullable<string>;
    assetRelatedText?: Nullable<string>;
}

export interface EmbedComponent {
    __typename?: "ContentPlatformEmbeddedVideoAsset" | "EmbeddedVideoAsset";
    embeddedVideoAssetName: Nullable<string>;
    embedded: Nullable<string>;
    embedAssetOverlay: Nullable<Image>;
}

export interface ProTipComponent {
    __typename?: "ContentPlatformProTipComponent" | "ProTipComponent";
    name: Nullable<string>;
    proTipText?: Nullable<string>;
}

export interface TwitterComponent {
    __typename?: "ContentPlatformTwitterComponent" | "TwitterComponent";
    twitterComponentName?: Nullable<string>;
    tweetText?: Nullable<string>;
    twitterUrl?: Nullable<string>;
}

export interface QuestionComponent {
    __typename?: "ContentPlatformQuestionComponent" | "QuestionComponent";
    answer?: Nullable<string>;
    questionTitle?: Nullable<string>;
}

export interface BodyTextContainer {
    name: string;
    contentComponents: ContentComponent[];
}

export type NextBestAction = Article | Tutorial;

export interface RelatedHub {
    title?: Nullable<string>;
    description?: Nullable<string>;
    shortDescriptionOneLiner?: Nullable<string>;
}

export interface AgileOverview {
    topicOverviewId: string;
    name: string;
    title: string;
    subtitle: Nullable<string>;
    description: Nullable<string>;
    bannerImage: Image;
    mainCallToAction: Nullable<CallToAction>;
    topicIntroduction: Nullable<TopicIntroduction[]>;
    featuredContentContainer: Nullable<FeaturedContentContainer[]>;
    bodyTextContainer: Nullable<BodyTextContainer[]>;
    nextBestAction: NextBestAction[];
    upNextFooter: Nullable<Article>;
    author: Nullable<Author>;
    urlSlug: string;
    relatedHub: RelatedHub[];
}

export interface ContentHubSubscribe {
    subscribeComponentName: Nullable<string>;
    valueProposition: Nullable<string>;
    ctaButtonText: Nullable<string>;
    successMessage: Nullable<string>;
}

export interface SocialMediaLink {
    socialMediaHandle: Nullable<string>;
    socialMediaUrl: Nullable<string>;
    socialMediaChannel: Nullable<SocialMediaChannel>;
}

export interface SocialMediaChannel {
    socialMediaChannel: Nullable<string>;
    logo: Nullable<Image>;
}

export interface TaxonomyFeature {
    id: Nullable<string>;
    name: Nullable<string>;
    shortDescriptionOneLiner: Nullable<string>;
    description: Nullable<string>;
}

export interface ArticleIntroduction {
    articleIntroductionName: Nullable<string>;
    articleIntroductionDetails: Nullable<string>;
    articleIntroductionAsset: Nullable<Image>;
    embedLink: Nullable<string>;
    componentizedIntroduction: Nullable<ContentComponent[]>;
}

export type AgileArticle = Article & {
    articleSummary: Nullable<string>;
    subtitle: Nullable<string>;
    author: Nullable<Author>;
    description: Nullable<string>;
    articleIntroduction: Nullable<ArticleIntroduction[]>;
    bodyTextContainer: Nullable<BodyTextContainer[]>;
    contentHubSubscribe: ContentHubSubscribe[];
    productDiscussedCta: Nullable<CallToAction[]>;
    relatedTutorialCta: Nullable<CallToAction[]>;
    whitePaperCta: Nullable<CallToAction[]>;
    upNext: Nullable<NextBestAction>;
    nextBestAction: Nullable<NextBestAction[]>;
    relatedHub: RelatedHub[];
    relatedProductFeatures: Nullable<TaxonomyFeature>;
    shareThisArticle: Nullable<SocialMediaLink>;
};

/**
 * Type guards for possible content component types
 */

export const isTextComponent = (
    component: ContentComponent
): component is TextComponent => {
    return (
        component.__typename === "ContentPlatformTextComponent" ||
        component.__typename === "TextComponent"
    );
};

export const isAssetComponent = (
    component: ContentComponent
): component is AssetComponent => {
    return (
        component.__typename === "ContentPlatformAssetComponent" ||
        component.__typename === "AssetComponent"
    );
};

export const isEmbedComponent = (
    component: ContentComponent
): component is EmbedComponent => {
    return (
        component.__typename === "ContentPlatformEmbeddedVideoAsset" ||
        component.__typename === "EmbeddedVideoAsset"
    );
};

export const isProTipText = (
    component: ContentComponent
): component is ProTipComponent => {
    return (
        component.__typename === "ContentPlatformProTipComponent" ||
        component.__typename === "ProTipComponent"
    );
};

export const isTwitterComponent = (
    component: ContentComponent
): component is TwitterComponent => {
    return (
        component.__typename === "ContentPlatformTwitterComponent" ||
        component.__typename === "TwitterComponent"
    );
};

export const isCallToAction = (
    component: ContentComponent
): component is CallToAction => {
    return (
        component.__typename === "ContentPlatformCallToAction" ||
        component.__typename === "CallToAction"
    );
};

export const isQuestionComponent = (
    component: ContentComponent
): component is QuestionComponent => {
    return (
        component.__typename === "ContentPlatformQuestionComponent" ||
        component.__typename === "QuestionComponent"
    );
};

export const isCallOutComponent = (
    component: ContentComponent
): component is CallOutComponent => {
    return (
        component.__typename === "ContentPlatformCallOutComponent" ||
        component.__typename === "CallOutComponent"
    );
};
